import { PlusOutlined, LoadingOutlined, EditTwoTone, EyeFilled } from "@ant-design/icons";
import { Spin, Table } from "antd";
import React from "react";
import CTConfiguration, { CTConfigurationCapitalAllowanceBase } from "../../Services/CTConfigurationService";
import dayjs, { Dayjs } from "dayjs";
import AddAIAs from "./AddAIAs";
import Link from "antd/es/typography/Link";
import { adminContext } from "../../Common/PageRoute";
import { useUser } from "../../Context/UserContext";
import { ColumnsType } from "antd/es/table";
import CustomRangePicker from "../../Common/Custom Components/Ant design/RangePicker";
import { TableToolbar } from "../../Common/Layout";

export interface CTConfigurationCapitalAIA extends CTConfigurationCapitalAllowanceBase {
  id?: string;
  amount: number;
}


const AnnualInvestmentAllowance = () => {
  const { openNotification } = React.useContext(adminContext);
  const [AIAsList, setAIAsList] = React.useState<CTConfigurationCapitalAIA[]>([]);
  const [filterdata, setFilterData] = React.useState<CTConfigurationCapitalAIA[]>([]);
  const [dialog, setDialog] = React.useState<{ type: string; params: any }>({
    type: "",
    params: {},
  });
  const [loading, setLoading] = React.useState(false);
  const [refresh, setRefresh] = React.useState(false);
  const { isSubAdmin } = useUser();
  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 15,
  });
  const [params, setParams] = React.useState<{
    fromDate: Dayjs | null,
    toDate: Dayjs | null
  }
  >({
    fromDate: null,
    toDate: null,
  })

  const columns: ColumnsType<CTConfigurationCapitalAIA> = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      width: "3%",
      render: (_: any, record: any, index: any) => <p>{(pagination.current - 1) * pagination.pageSize + index + 1}</p>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "20%",
      sorter: (a, b) => a?.name?.localeCompare(b.name),
    },
    {
      title: "Form date",
      dataIndex: "fromDate",
      key: "fromDate",
      width: "20%",
      render: (_: any, record: any, index: any) => (
        <p>
          {record.fromDate !== undefined && record.fromDate !== null
            ? dayjs(record.fromDate).format("DD/MM/YYYY")
            : "-"}
        </p>
      ),
      sorter: (a, b) => dayjs(a.fromDate).unix() - dayjs(b.fromDate).unix(),
    },
    {
      title: "To date",
      dataIndex: "toDate",
      key: "toDate",
      width: "20%",
      render: (_: any, record: any, index: any) => (
        <p>
          {record.toDate !== undefined && record.toDate !== null
            ? dayjs(record.toDate).format("DD/MM/YYYY")
            : "-"}
        </p>
      ),
      sorter: (a, b) => dayjs(a.toDate).unix() - dayjs(b.toDate).unix(),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "20%",
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "Id",
      width: "7%",
      render: (_: any, record: any, index: any) => (
        <Link
          className="edit-btn"
          onClick={() => {
            if (record.id !== undefined || record.id !== null) {
              setDialog({
                type: "addAIAs",
                params: { id: record.id, record: record },
              });
            } else {
              openNotification("error", "Id not Found")
            }
          }}
        >
          {
            isSubAdmin() ?
              <EyeFilled />
              :
              <EditTwoTone />
          }
        </Link>
      ),
    },
  ];

  React.useEffect(() => {
    const getRnDList = async () => {
      setLoading(true);
      let res = await CTConfiguration.getAIAs();
      if (res.status) {
        setAIAsList(res.result);
        setLoading(false);
      } else {
        setLoading(false);
      }
    };
    getRnDList();
  }, [refresh]);

  React.useEffect(() => {
    let data = [...AIAsList];

    /* -------- Date Range Filter -------- */
    const hasDateRange = params.fromDate && params.toDate;
    if (hasDateRange) {
      data = data.filter((x: CTConfigurationCapitalAIA) => {
        let dateMatch = true;
        if (hasDateRange) {
          const recordFrom = dayjs(x.fromDate);
          const recordTo = dayjs(x.toDate);

          dateMatch =
            recordFrom.isSameOrBefore(params.toDate, "day") &&
            recordTo.isSameOrAfter(params.fromDate, "day");
        }
        return dateMatch;
      });
    }
    setFilterData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.fromDate, params.toDate, AIAsList]);

  const primaryActions: any[] = [];
  if (!isSubAdmin()) {
    primaryActions.push({
      label: "AIAs",
      icon: <PlusOutlined />,
      type: "primary",
      onClick: () => setDialog({ type: "addAIAs", params: {} }),
    });
  }

  return (
    <div>
      <div>
        <TableToolbar
          primaryActions={primaryActions}
          extra={
            <CustomRangePicker
              value={[params.fromDate, params.toDate]}
              onChange={(dates) => {
                setParams(prev => ({
                  ...prev,
                  fromDate: dates?.[0] || null,
                  toDate: dates?.[1] || null,
                }));
              }}
            />
          }
        />
      </div>
      <Table
        dataSource={filterdata?.length > 0 ? filterdata : []}
        columns={columns}
        size="small"
        className="border-top-table"
        loading={{ spinning: loading, indicator: <Spin indicator={<LoadingOutlined />} /> }}
        locale={{
          triggerDesc: "",
          triggerAsc: "",
          cancelSort: "",
        }}
        pagination={{
          defaultPageSize: 15,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          showSizeChanger: true,
          total: filterdata?.length,
          pageSizeOptions: ["15", "25", "50", "100"],
          onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
        }}
        scroll={{ x: 768 }}
      />

      {dialog?.type === "addAIAs" && (
        <AddAIAs
          isViewOnly={isSubAdmin()}
          id={dialog.params?.id || ""}
          record={dialog.params?.record}
          onDismiss={(conformation: any) => {
            setDialog({ type: "", params: {} });
            if (conformation) {
              setRefresh((x) => x = !x);
            }
          }}
        />
      )}
    </div>
  );
};

export default AnnualInvestmentAllowance;
